import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { DEFAULT_FIN, CUR_YEAR, MonthSelect } from "./shared";

export function NewMeetingDialog({ companyId }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", type: "styremote", date: "", location: "Teams", financials: { ...DEFAULT_FIN } });

  const create = async () => {
    if (!form.title || !form.date) {
      toast.error("Fyll inn tittel og dato");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/meetings`, form);
      toast.success("Møte opprettet");
      setOpen(false);
      setForm({ title: "", type: "styremote", date: "", location: "Teams", financials: { ...DEFAULT_FIN } });
      navigate(`/selskap/${companyId}/moter/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-meeting-btn" className="gap-2 active:scale-[0.98]"><Plus className="h-4 w-4" /> Nytt møte</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nytt møte</DialogTitle>
          <DialogDescription>Opprett et nytt styremøte eller en generalforsamling.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Tittel</Label>
            <Input data-testid="meeting-title-input" className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Styremøte 3/2026" />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="meeting-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="styremote">Styremøte</SelectItem>
                <SelectItem value="generalforsamling">Generalforsamling</SelectItem>
                <SelectItem value="ekstraordinar_gf">Ekstraordinær generalforsamling</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Dato og tid</Label>
              <Input data-testid="meeting-date-input" type="datetime-local" className="mt-1.5" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label>Sted</Label>
              <Input className="mt-1.5" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
          </div>

          <div className="rounded-lg border border-border/60 bg-muted/30 p-4">
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                data-testid="meeting-financials-toggle"
                checked={form.financials.enabled}
                onCheckedChange={(v) => setForm({ ...form, financials: { ...form.financials, enabled: !!v } })}
                className="mt-0.5"
              />
              <span>
                <span className="text-sm font-medium">Ta med nøkkeltall og kort analyse</span>
                <span className="block text-xs text-muted-foreground">Viser inntekter, kostnader og resultat for valgt periode, med en AI-analyse.</span>
              </span>
            </label>

            {form.financials.enabled ? (
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Periode</Label>
                    <Select value={form.financials.mode} onValueChange={(v) => setForm({ ...form, financials: { ...form.financials, mode: v } })}>
                      <SelectTrigger data-testid="fin-mode-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="range">Måned-intervall</SelectItem>
                        <SelectItem value="single">Én måned</SelectItem>
                        <SelectItem value="ytd">Hittil i år</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">År</Label>
                    <Select value={String(form.financials.year)} onValueChange={(v) => setForm({ ...form, financials: { ...form.financials, year: Number(v) } })}>
                      <SelectTrigger data-testid="fin-year-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={String(CUR_YEAR - 1)}>{CUR_YEAR - 1}</SelectItem>
                        <SelectItem value={String(CUR_YEAR)}>{CUR_YEAR}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {form.financials.mode === "range" ? (
                  <div className="grid grid-cols-2 gap-3">
                    <MonthSelect label="Fra måned" testid="fin-from-month" value={form.financials.from_month}
                      onChange={(mn) => setForm({ ...form, financials: { ...form.financials, from_month: mn } })} />
                    <MonthSelect label="Til måned" testid="fin-to-month" value={form.financials.to_month}
                      onChange={(mn) => setForm({ ...form, financials: { ...form.financials, to_month: mn } })} />
                  </div>
                ) : form.financials.mode === "single" ? (
                  <MonthSelect label="Måned" testid="fin-single-month" value={form.financials.to_month}
                    onChange={(mn) => setForm({ ...form, financials: { ...form.financials, from_month: mn, to_month: mn } })} />
                ) : (
                  <MonthSelect label="Til og med måned" testid="fin-ytd-month" value={form.financials.to_month}
                    onChange={(mn) => setForm({ ...form, financials: { ...form.financials, from_month: 1, to_month: mn } })} />
                )}
              </div>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Avbryt</Button>
          <Button data-testid="save-meeting-btn" onClick={create} disabled={saving}>{saving ? "Lagrer …" : "Opprett møte"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
